import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { CompanyService } from './company.service';

export interface OAuthConnectResponse {
  authorizationUrl: string;
  state: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface OAuthCallbackResponse {
  linked: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  private readonly baseUrl = environment.baseBackendUrl;

  selectedDataSourceProviderId = 7;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private companyService: CompanyService
  ) {}

  /**
   * Inicia el proceso de conexión OAuth para un proveedor específico
   * @param providerId ID del proveedor de datos
   * @returns Observable con la URL de autorización y el estado
   */
  connect(providerId: number): Observable<OAuthConnectResponse> {
    sessionStorage.setItem('mojitoOatuhRedirectReportUrl', window.location.href);
    return this.http.get<OAuthConnectResponse>(`${this.baseUrl}/oauth/connect/${providerId}`, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Maneja el callback de OAuth después de la autorización
   * @param params Parámetros del callback (code y state)
   * @returns Observable con la respuesta del callback
   */
  callback(params: OAuthCallbackParams): Observable<OAuthCallbackResponse> {
    const queryParams = new HttpParams()
      .set('code', params.code)
      .set('state', params.state);

    return this.http.get<OAuthCallbackResponse>(`${this.baseUrl}/oauth/callback`, {
      params: queryParams,
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();

    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }
}
